import { AmountBasedConditionTypes, REWARD_METER_KEY, RewardsLevelColor, RewardsLevelColors } from "../constants";
import { CashbackClaimingFrequency, LevelConditionType } from "../enums";
import { CashbackLevel, RewardMeterLocal } from "../models";
import { dotFormatter } from "./number";

export const getRewardMeterData = (): Array<RewardMeterLocal> => {
  const setCashback = localStorage.getItem(REWARD_METER_KEY);
  if (!setCashback) return [];
  try {
    return JSON.parse(setCashback);
  } catch {
    return [];
  }
}

// Save to localStorage
export const saveRewardMeterData = (data: Array<RewardMeterLocal>) => {
  localStorage.setItem(REWARD_METER_KEY, JSON.stringify(data));
}

// Cleanup expired cashback
export const cleanExpiredCashbacks = () => {
  const today = new Date().toISOString().slice(0, 10);
  const data = getRewardMeterData();
  const isAlreadyCleaned = data.filter(cashbackInfo => !cashbackInfo.endDate || cashbackInfo.endDate >= today);
  if (isAlreadyCleaned.length !== data.length) {
    saveRewardMeterData(isAlreadyCleaned);
  }
}

// Check if popup should show
export const shouldShowPopup = (cashbackId: number): boolean => {
  const today = new Date().toISOString().slice(0, 10);
  const data = getRewardMeterData();

  const item = data.find(cashbackInfo => cashbackInfo.cashbackId === cashbackId);
  if (!item) return true;
  if (item.dontShowAgain) return false;
  if (item.seenDate === today) return false;
  return true;
}

// Mark popup as seen
export const markPopupSeen = (cashbackId: number, dontShowAgain = false, endDate: string) => {
  const today = new Date().toISOString().slice(0, 10);
  const data = getRewardMeterData();
  const idx = data.findIndex(d => d.cashbackId === cashbackId);
  const newItem: RewardMeterLocal = { cashbackId, seenDate: today, dontShowAgain, endDate };

  if (idx > -1) {
    data[idx] = newItem;
  } else {
    data.push(newItem);
  }
  saveRewardMeterData(data);
}

const FrequncyTypeEndDates = {
  [CashbackClaimingFrequency.Daily]: () => {
    // Start of the Next Day
    const now = new Date();
    // We just add 1 to the current date and reset the clock to midnight
    const startOfNextDay = new Date(now);
    startOfNextDay.setDate(now.getDate() + 1);
    startOfNextDay.setHours(0, 0, 0, 0);
    return startOfNextDay;
  },
  [CashbackClaimingFrequency.Weekly]: () => {
    // Start of the Next Week (Assuming Monday is the start of the week)
    const now = new Date();
    const startOfNextWeek = new Date(now);
    // getDay() returns 0 for Sunday, 1 for Monday, etc.
    // Calculate days until next Monday
    const currentDay = now.getDay();
    const daysUntilNextMonday = currentDay === 0 ? 1 : 8 - currentDay;

    startOfNextWeek.setDate(now.getDate() + daysUntilNextMonday);
    startOfNextWeek.setHours(0, 0, 0, 0);
    return startOfNextWeek;
  },
  [CashbackClaimingFrequency.Monthly]: () => {
    // Start of the Next Month
    const now = new Date();
    // We pass the current year and the NEXT month index. 
    // Setting the day parameter to 1 gives us the 1st of that month.
    const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    startOfNextMonth.setHours(0, 0, 0, 0);
    return startOfNextMonth;
  }
}

export const getFrequencyEndDate = (frequency: CashbackClaimingFrequency): Date => {
  return FrequncyTypeEndDates[frequency]();
}

// Contiguous blocks over the palette: 8 levels -> 2 per color; 4 -> one each; 2 -> green, orange.
export const getRewardsLevelColor = (levelIndex: number, levelCount: number): RewardsLevelColor => {
  if (levelCount <= 0) return RewardsLevelColors[0];
  const colorIndex = Math.min(
    Math.floor((levelIndex * RewardsLevelColors.length) / levelCount),
    RewardsLevelColors.length - 1
  );
  return RewardsLevelColors[colorIndex];
}

export const isAmountBasedCondition = (type: LevelConditionType): boolean => {
  return AmountBasedConditionTypes.includes(type);
}

// wrapClassName: undefined -> plain text; '' -> bare <span>; non-empty -> <span class="...">
export const getLevelConditionValues = (level: CashbackLevel, currency: string, wrapClassName?: string): Record<string, string> => {
  const wrap = (value: string): string => {
    if (wrapClassName === undefined) return value;
    return `<span${wrapClassName ? ` class="${wrapClassName}"` : ''}>${value}</span>`;
  };
  return {
    amount: wrap(`${dotFormatter(level.goalAmount)} ${currency}`),
    count: wrap(dotFormatter(level.goalCount)),
    multiplier: wrap(`${level.multiplier ?? ''}`)
  };
}
